import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Col,
  Row,
  Container,
  Card,
  InputGroup,
  Breadcrumb,
} from "react-bootstrap";
import "./Checkout.css";
import { Link, useLocation } from "react-router-dom";
import razorPay from "../../assets/images/razorpay.png";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import SecureLS from "secure-ls";
import axios from "axios";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ls = new SecureLS({ encodingType: "aes" });
  const [iscreateAccount, setIscreateAccount] = useState(false);
  const keyId = process.env.REACT_APP_KEY_ID_PROD;
  // const keyId = process.env.REACT_APP_KEY_ID_DEV;
  const [paymentMethod, setPaymentMethod] = useState("payNow"); // Default to 'payNow'
  const [codEligible, setCodEligible] = useState(true);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    scrollToTop();
    const username = ls.get("userEmail");
    if (!username) {
      setIscreateAccount(true);
    } else {
      getMyDetails(username);
    }
  }, [navigate]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script); // Cleanup on unmount
    };
  }, []);

  const getMyDetails = async (username) => {
    try {
      const email = ls.get("userEmail");
      if (email) {
        const response = await axios.get(
          `https://karstar.in/api/client_api/authentication/account/index.php?email=${encodeURIComponent(
            username
          )}`
        );
        const myDetails = response.data;
        setFormValues((prevValues) => ({
          ...prevValues,
          email: myDetails.Email_Id || "",
          phone: myDetails.Mobile_No || "",
          firstName: myDetails.Firstname || "",
          lastName: myDetails.Lastname || "",
          address: myDetails.address1 || "",
          address2: myDetails.address2 || "",
          city: myDetails.city || "",
          state: myDetails.states || "",
          zip: myDetails.Zip || "",
        }));
      } else {
        console.error("User email is not found in local storage.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const { isBuyNow, isCart, selectedSize } = location.state || {};

  const [item, setItem] = useState(() => {
    let initial = location.state ? location.state.item : undefined;
    initial = Array.isArray(initial) ? initial : [initial];
    initial = initial.filter(Boolean);

    if (isBuyNow && selectedSize) {
      initial = initial.map((product) => {
        const sizePrice = product.price.find(
          (priceEntry) =>
            priceEntry.weight + priceEntry.abbrevation === selectedSize
        );
        return { ...product, price: sizePrice, qty: 1 };
      });
    }

    return initial;
  });

  const hasItems = item.length > 0 && item.every((p) => p && p.price);

  const baseUrl = "https://karstar.in/";

  // Nobody landed here with a valid cart/buy-now selection (e.g. a direct
  // visit to /checkout) — send them back instead of crashing on missing data.
  useEffect(() => {
    if (!hasItems) {
      navigate("/cart");
    }
  }, [hasItems, navigate]);

  // Refresh price, delivery, and discount config from the live catalog on
  // load — the item data arrives via navigation state and can otherwise go
  // stale if it was added to cart before a price/discount change.
  useEffect(() => {
    if (!hasItems) return;
    const prodcodes = item
      .map((p) => p.price && p.price.prodcode)
      .filter(Boolean);
    if (prodcodes.length === 0) return;

    axios
      .post(
        "https://karstar.in/api/client_api/products/refreshPricing/index.php",
        { prodcodes }
      )
      .then((response) => {
        const fresh = response.data || {};
        setItem((prevItems) =>
          prevItems.map((product) => {
            const prodcode = product.price && product.price.prodcode;
            const freshData = prodcode ? fresh[prodcode] : null;
            if (!freshData) return product;
            return {
              ...product,
              price: { ...product.price, ...freshData.price },
              delivery: freshData.delivery || product.delivery,
            };
          })
        );
      })
      .catch((error) => {
        console.error("Error refreshing pricing:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalQuantity = item.reduce(
    (total, item) => total + parseInt(item.qty ? item.qty : 1),
    0
  );
  const totalMRP = item.reduce(
    (total, item) =>
      total +
      (parseFloat(item.price?.basePrice) || 0) *
        parseInt(item.qty ? item.qty : 1),
    0
  );
  const totalDiscount = item.reduce(
    (total, item) =>
      total +
      ((parseFloat(item.price?.basePrice) || 0) -
        (parseFloat(item.price?.discountedPrice) || 0)) *
        parseInt(item.qty ? item.qty : 1),
    0
  );
  const totalAmount = item.reduce(
    (total, item) =>
      total +
      (parseFloat(item.price?.discountedPrice) || 0) *
        parseInt(item.qty ? item.qty : 1),
    0
  );

  const [formValues, setFormValues] = useState({
    email: "",
    emailValid: null,
    phone: "",
    phoneValid: null,
    firstName: "",
    firstNameValid: null,
    lastName: "",
    lastNameValid: null,
    country: "India",
    address: "",
    addressValid: null,
    address2: "",
    city: "",
    cityValid: null,
    state: "",
    stateValid: null,
    zip: "",
    zipValid: null,
    password: "",
    passwordValid: null,
    confirmPassword: "",
    confirmPasswordValid: null,
    confirmPasswordMatch: null,
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [shippingPrice, setShippingPrice] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discountAmount }
  const [couponMessage, setCouponMessage] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Show/hide password toggle state
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Show/hide confirm password toggle
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState("");

  useEffect(() => {
    const {
      email,
      phone,
      firstName,
      lastName,
      address,
      city,
      state,
      zip,
      password,
      confirmPassword,
    } = formValues;
    if (
      validateEmail(email) &&
      validatePhoneNumber(phone) &&
      validateRequired(firstName) &&
      validateRequired(lastName) &&
      validateRequired(address) &&
      validateRequired(city) &&
      validateRequired(state) &&
      validateRequired(zip) &&
      (!createAccount ||
        (validateRequired(password) &&
          validateRequired(confirmPassword) &&
          validatePasswordMatch(password, confirmPassword)))
    ) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [formValues, createAccount]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const re = /^\d{10}$/;
    return re.test(phone);
  };

  const validateZipCode = (zip) => {
    const re = /^\d{6}$/;
    return re.test(zip);
  };

  const validateRequired = (value) => {
    return value.trim() !== "";
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    handleConfirmPasswordChange(password, confirmPassword);
    return password === confirmPassword;
  };

  const calculatePasswordStrength = (password) => {
    setShowPasswordStrength(password.length > 0);
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);

    if (strength === 5) {
      setPasswordStrengthText("Strong");
    } else if (strength >= 3) {
      setPasswordStrengthText("Medium");
    } else {
      setPasswordStrengthText("Weak");
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    let isValid = false;

    switch (id) {
      case "email":
        isValid = validateEmail(value);
        break;
      case "phone":
        isValid = validatePhoneNumber(value);
        break;
      case "zip":
        isValid = validateZipCode(value);
        break;
      default:
        isValid = validateRequired(value);
        break;
    }

    setFormValues((prevState) => ({
      ...prevState,
      [id]: value,
      [`${id}Valid`]: isValid,
    }));
  };

  const handleCheckboxChange = (e) => {
    setCreateAccount(e.target.checked);
  };

  // Toggle show/hide password
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleConfirmPasswordChange = (password, confirmPassword) => {
    if (password === confirmPassword) {
      setPasswordMatchError("Passwords do not match.");
    } else {
      setPasswordMatchError("");
    }
  };

  const fetchShippingPrice = async (zip) => {
    try {
      // Extract the prefix from the ZIP code
      const zipPrefix = zip.slice(0, 2); // Assuming ZIP code is a string

      // Check if the zipPrefix is between 56 to 59
      const isKarnataka = zipPrefix >= "56" && zipPrefix <= "59";

      // Function to get delivery price based on option
      const getDeliveryPrice = (deliveryOptions, option) => {
        const optionObj = deliveryOptions.find(
          (o) => o.option.toLowerCase() === option.toLowerCase()
        );
        const price = optionObj ? parseFloat(optionObj.values[0]) : 0;
        return Number.isNaN(price) ? 0 : price;
      };

      // Calculate the total shipping price
      const shipping = item.reduce((acc, item) => {
        const deliveryOptions = item.delivery;
        // console.log(deliveryOptions);
        const deliveryPrice = isKarnataka
          ? getDeliveryPrice(deliveryOptions, "karnataka")
          : getDeliveryPrice(deliveryOptions, "Non-Karnataka");

        // Multiply by quantity
        return acc + deliveryPrice * (item.qty ? parseInt(item.qty) : 1);
      }, 0);

      setShippingPrice(shipping);
    } catch (error) {
      console.error("Error fetching shipping price:", error);
    }
  };

  useEffect(() => {
    if (formValues.zip) {
      fetchShippingPrice(formValues.zip);
    }
  }, [formValues.zip]);

  const hasValidZip = !!formValues.zip && formValues.zip.length === 6;
  const zipPrefix = hasValidZip ? formValues.zip.slice(0, 2) : "";
  const isKarnataka = hasValidZip && zipPrefix >= "56" && zipPrefix <= "59";

  // Reads a per-state discount config out of item.price.onlineDiscount,
  // tolerating legacy data saved as a plain flat number (non-Karnataka only).
  const getDiscountConfig = (onlineDiscount, state) => {
    if (onlineDiscount && typeof onlineDiscount === "object" && onlineDiscount[state]) {
      const cfg = onlineDiscount[state];
      return {
        enabled: cfg.enabled === true || cfg.enabled === "1" || cfg.enabled === 1,
        type: cfg.type || "flat",
        value: parseFloat(cfg.value) || 0,
        maxDiscount:
          cfg.maxDiscount !== "" && cfg.maxDiscount != null
            ? parseFloat(cfg.maxDiscount)
            : null,
      };
    }
    if (state === "nonKarnataka" && onlineDiscount && typeof onlineDiscount !== "object") {
      const legacyValue = parseFloat(onlineDiscount) || 0;
      return { enabled: legacyValue > 0, type: "flat", value: legacyValue, maxDiscount: null };
    }
    return { enabled: false, type: "flat", value: 0, maxDiscount: null };
  };

  const computeItemDiscount = (lineSubtotal, qty, config) => {
    if (!config.enabled) return 0;
    if (config.type === "percent") {
      const raw = lineSubtotal * (config.value / 100);
      return config.maxDiscount != null ? Math.min(raw, config.maxDiscount) : raw;
    }
    return config.value * qty; // flat, per unit
  };

  // Paying online applies each product's configured online-payment discount,
  // for whichever state (Karnataka/Non-Karnataka) the address falls into.
  // Products with no discount enabled for that state simply contribute 0.
  // Until a full 6-digit pincode is entered, the state is unknown, so no
  // discount is shown yet.
  const onlineDiscountTotal = hasValidZip
    ? item.reduce((total, product) => {
        const qty = parseInt(product.qty ? product.qty : 1);
        const state = isKarnataka ? "karnataka" : "nonKarnataka";
        const config = getDiscountConfig(product.price.onlineDiscount, state);
        const lineSubtotal = parseFloat(product.price.discountedPrice) * qty;
        return total + computeItemDiscount(lineSubtotal, qty, config);
      }, 0)
    : 0;

  const codTotal = totalAmount + shippingPrice;
  const onlineTotal = codTotal - onlineDiscountTotal;
  const displayedTotal = paymentMethod === "payNow" ? onlineTotal : codTotal;

  // Coupons are only available for online payment (not Cash on Delivery),
  // and even then can't combine with an active online-payment discount.
  const couponEligible = paymentMethod === "payNow" && onlineDiscountTotal === 0;
  const couponDiscountTotal =
    appliedCoupon && couponEligible ? appliedCoupon.discountAmount : 0;
  const finalTotal = displayedTotal - couponDiscountTotal;

  // useEffect(() => {
  //   if (formValues.zip) {
  //     checkCodEligibility(formValues.zip).then((eligibility) => {
  //       setCodEligible(eligibility);
  //     });
  //   }
  // }, [formValues.zip]);

  // const checkCodEligibility = async (pincode) => {
  //   try {
  //     const response = await axios.get(
  //       `https://karstar.in/api/client_api/getcodpincode/index.php?pincode=${pincode}`
  //     );
  //     return response.data.pincode === pincode;
  //   } catch (error) {
  //     console.error("Error fetching COD eligibility:", error);
  //   }
  // };

  // Function to handle payment method change
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    const staysEligible = method === "payNow" && onlineDiscountTotal === 0;
    if (!staysEligible && appliedCoupon) {
      setAppliedCoupon(null);
      setCouponCode("");
      setCouponMessage("");
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      return;
    }
    setCouponLoading(true);
    setCouponMessage("");
    try {
      const couponItems = item.map((product) => ({
        id: product.id,
        collection: product.collection,
        qty: product.qty ? parseInt(product.qty) : 1,
        lineTotal:
          parseFloat(product.price.discountedPrice) *
          (product.qty ? parseInt(product.qty) : 1),
      }));

      const response = await axios.post(
        "https://karstar.in/api/client_api/coupon/validate.php",
        {
          code: couponCode.trim(),
          items: couponItems,
        }
      );

      if (response.data.valid) {
        setAppliedCoupon({
          code: response.data.code,
          discountAmount: response.data.discountAmount,
        });
        setCouponMessage("");
      } else {
        setAppliedCoupon(null);
        setCouponMessage(response.data.message || "Invalid coupon code.");
      }
    } catch (error) {
      setAppliedCoupon(null);
      setCouponMessage("Error validating coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponMessage("");
  };

  const handlePayment = async () => {
    const sessionId = sessionStorage.getItem("sessionId");
    const username = ls.get("userEmail");

    // Step 1: Attempt user registration if needed
    if (createAccount) {
      try {
        const registerResponse = await fetch(
          "https://karstar.in/api/client_api/authentication/register/checkoutregister.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ formValues }),
          }
        );

        const registerData = await registerResponse.json();

        if (!registerData.success) {
          alert(registerData.message || "An error occurred");
          return; // Stop further execution if registration fails
        }
      } catch (error) {
        console.log("An error occurred during registration: " + error.message);
        return; // Stop further execution if an exception occurs during registration
      }
    }

    const orderItems = item.map((product) => ({
      id: product.id,
      prodcode: product.price.prodcode,
      collection: product.collection,
      qty: product.qty ? parseInt(product.qty) : 1,
    }));

    const couponToApply = couponEligible ? appliedCoupon?.code : undefined;

    // Steo 2: check the payment method
    if (paymentMethod === "payNow") {
      // Step 3: Create the order (backend recomputes the authoritative total,
      // applying the online-payment discount only for non-Karnataka addresses)
      try {
        const response = await fetch(
          "https://karstar.in/api/client_api/gateway/createorder.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: orderItems,
              paymentMethod: "online",
              zip: formValues.zip,
              couponCode: couponToApply,
            }),
          }
        );

        const data = await response.json();
        // console.log(data);

        // if (!data.success) {
        //     alert('Order creation failed: ' + (data.error || 'An error occurred'));
        //     return; // Stop further execution if order creation fails
        // }

        const options = {
          key: keyId,
          amount: Math.round(data.amount * 100), // Amount in paise, per server-computed total
          currency: "INR",
          name: "Karstar",
          description: "You are making payment for an order on karstar website",
          order_id: data.orderId,
          handler: async function (response) {
            try {
              const orderResponse = await fetch(
                "https://karstar.in/api/client_api/place/placeorder.php",
                {
                  method: "POST",
                  body: JSON.stringify({
                    items: item,
                    formValues,
                    paid: data.amount,
                    delivery_charges: shippingPrice,
                    couponCode: couponToApply,
                    paymentId: response.razorpay_payment_id,
                    orderId: data.customOrderId, // Use the custom order ID
                    razorpayOrderId: response.razorpay_order_id, // Add the Razorpay order ID,
                    isBuyNow: isBuyNow,
                    isCart: isCart,
                    sessionId: sessionId || "na",
                    username: username || "na",
                  }),
                }
              );

              const orderData = await orderResponse.json();

              // console.log(orderResponse);

              if (orderData.success) {
                navigate("/placed", {
                  state: {
                    orderId: data.customOrderId,
                    address: formValues,
                    totalAmount: data.amount,
                    orderDate: "2024-07-18",
                    items: item,
                  },
                });

                try {
                  const orderid = data.customOrderId;
                  const response = await axios.get(
                    "https://admin.karstar.in/api/v1/notifications/w3send.php",
                    {
                      params: { orderid }, // Passing the orderId as a query parameter
                    }
                  );

                  const registerData = response.data;

                  if (!registerData.success) {
                    alert(registerData.message || "An error occurred");
                    return; // Stop further execution if registration fails
                  }
                } catch (error) {
                  console.log(
                    "An error occurred during registration: " + error.message
                  );
                  return; // Stop further execution if an exception occurs during registration
                }
              } else {
                alert(
                  "Failed to save order: " +
                    (orderData.error || "An error occurred")
                );
              }
            } catch (error) {
              alert(
                "An error occurred while placing the order: " + error.message
              );
            }
          },
          prefill: {
            name: `${formValues.firstName} ${formValues.lastName}`,
            email: formValues.email,
            contact: formValues.phone,
          },
          notes: {
            address: formValues.address,
          },
          theme: {
            color: "#3399cc",
          },
        };

        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      } catch (error) {
        alert("An error occurred during order creation: " + error.message);
      }
    } else {
      try {
        const response = await fetch(
          "https://karstar.in/api/client_api/gateway/createorder.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: orderItems,
              paymentMethod: "cod",
              zip: formValues.zip,
              couponCode: couponToApply,
            }),
          }
        );

        const data = await response.json();

        const codResponse = await fetch(
          "https://karstar.in/api/client_api/place/placeorder.php",
          {
            method: "POST",
            body: JSON.stringify({
              items: item,
              formValues,
              paid: data.amount,
              delivery_charges: shippingPrice,
              couponCode: couponToApply,
              paymentId: response.razorpay_payment_id ?? "COD",
              orderId: data.customOrderId, // Use the custom order ID
              razorpayOrderId: "COD", // Add the Razorpay order ID,
              isBuyNow: isBuyNow,
              isCart: isCart,
              sessionId: sessionId || "na",
              username: username || "na",
            }),
          }
        );

        const codData = await codResponse.json();

        if (codData.success) {
          navigate("/placed", {
            state: {
              orderId: data.customOrderId,
              address: formValues,
              totalAmount: data.amount,
              orderDate: "2024-07-18",
              items: item,
            },
          });

          // try {
          //   const orderid = data.customOrderId;
          //   const response = await axios.get(
          //     "https://admin.karstar.in/api/v1/notifications/w3send.php",
          //     {
          //       params: { orderid }, // Passing the orderId as a query parameter
          //     }
          //   );

          //   const registerData = response.data;

          //   if (!registerData.success) {
          //     alert(registerData.message || "An error occurred");
          //     return; // Stop further execution if registration fails
          //   }
          // } catch (error) {
          //   console.log(
          //     "An error occurred during registration: " + error.message
          //   );
          //   return; // Stop further execution if an exception occurs during registration
          // }
        } else {
          alert(
            "Failed to save order: " + (codData.error || "An error occurred")
          );
        }
      } catch (error) {
        console.log("An error occurred while placing the order: " + error);
      }
    }
  };

  return (
    <div>
      <section className="sectionView pb-5 mt-3">
        <Container>
          <div className="breadcrumb-container">
            <div className="breadcrumb">
              <Link to="/" className="breadcrumb-item text-black">
                Home
              </Link>
              <Link to="/Cart" className="breadcrumb-item text-black">
                My Cart
              </Link>
              <div className="breadcrumb-item text-black">Checkout</div>
            </div>
          </div>
          <Row className="g-2">
            <Col lg={7}>
              <Card>
                <Card.Body>
                  <Form>
                    {/* Contact Section */}
                    <h5 className="fr1 text-start">Contact Info</h5>
                    <Row>
                      <Col md={6} lg={6} sm={12} className="text-start">
                        <Form.Group controlId="email">
                          <Form.Label className="mt-2 fr05">Email</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>@</InputGroup.Text>
                            <Form.Control
                              type="email"
                              className={`bg-light rounded-end-2 ${
                                formValues.emailValid === false
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Enter e-mail address"
                              value={formValues.email}
                              onChange={handleChange}
                            />
                            {formValues.emailValid === false && (
                              <div className="invalid-feedback text-start">
                                Please enter a valid email address.
                              </div>
                            )}
                          </InputGroup>
                        </Form.Group>
                      </Col>
                      <Col md={6} lg={6} sm={12} className="text-start">
                        <Form.Group controlId="phone">
                          <Form.Label className="mt-2 fr05">Phone</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>+91</InputGroup.Text>
                            <Form.Control
                              type="text"
                              className={`bg-light rounded-end-2 ${
                                formValues.phoneValid === false
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Enter mobile number"
                              value={formValues.phone}
                              onChange={handleChange}
                            />
                            {formValues.phoneValid === false && (
                              <div className="invalid-feedback text-start">
                                Please enter a valid 10 Digit phone number
                                without +91.{" "}
                              </div>
                            )}
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr />
                    {/* Personal Info Section */}
                    <h5 className="fr1 text-start">Personal Info</h5>
                    <Row>
                      <Col md={6} lg={6} sm={12} className="text-start">
                        <Form.Group controlId="firstName">
                          <Form.Label className="mt-2 fr05">
                            First Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className={`bg-light rounded-end-2 ${
                              formValues.firstNameValid === false
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="First name"
                            value={formValues.firstName}
                            onChange={handleChange}
                          />
                          {formValues.firstNameValid === false && (
                            <div className="invalid-feedback text-start">
                              First name is required.
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6} lg={6} sm={12} className="text-start">
                        <Form.Group controlId="lastName">
                          <Form.Label className="mt-2 fr05">
                            Last Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className={`bg-light rounded-end-2 ${
                              formValues.lastNameValid === false
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Last name"
                            value={formValues.lastName}
                            onChange={handleChange}
                          />
                          {formValues.lastNameValid === false && (
                            <div className="invalid-feedback text-start">
                              Last name is required.
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr />

                    {/* Delivery Section */}
                    <h5 className="fr1 text-start">Delivery</h5>

                    <Form.Group controlId="country" className="text-start">
                      <Form.Label className="fr05">Country/Region</Form.Label>
                      <Form.Control
                        as="select"
                        value={formValues.country}
                        onChange={handleChange}
                      >
                        <option>India</option>
                        {/* Add more options as needed */}
                      </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="address" className="text-start">
                      <Form.Label className="mt-2 fr05">Address</Form.Label>
                      <Form.Control
                        type="text"
                        className={`bg-light rounded-end-2 ${
                          formValues.addressValid === false ? "is-invalid" : ""
                        }`}
                        placeholder="Door no, street address etc"
                        value={formValues.address}
                        onChange={handleChange}
                      />
                      {formValues.addressValid === false && (
                        <div className="invalid-feedback text-start">
                          Address is required.
                        </div>
                      )}
                    </Form.Group>
                    <Form.Group controlId="address2" className="text-start">
                      <Form.Label className="mt-2 fr05">
                        Apartment, suite, etc. (optional)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formValues.address2}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Row>
                      <Col md={4} lg={4} sm={12} className="text-start">
                        <Form.Group controlId="city">
                          <Form.Label className="mt-2 fr05">City</Form.Label>
                          <Form.Control
                            type="text"
                            className={`bg-light rounded-end-2 ${
                              formValues.cityValid === false ? "is-invalid" : ""
                            }`}
                            placeholder="City"
                            value={formValues.city}
                            onChange={handleChange}
                          />
                          {formValues.cityValid === false && (
                            <div className="invalid-feedback text-start">
                              City is required.
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4} lg={4} sm={12} className="text-start">
                        <Form.Group controlId="state">
                          <Form.Label className="mt-2 fr05">State</Form.Label>
                          <Form.Control
                            type="text"
                            className={`bg-light rounded-end-2 ${
                              formValues.stateValid === false
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="State"
                            value={formValues.state}
                            onChange={handleChange}
                          />
                          {formValues.stateValid === false && (
                            <div className="invalid-feedback text-start">
                              State is required.
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4} lg={4} sm={12} className="text-start">
                        <Form.Group controlId="zip">
                          <Form.Label className="mt-2 fr05">
                            PIN Code
                          </Form.Label>
                          <Form.Control
                            type="number"
                            maxLength={6}
                            minLength={6}
                            className={`bg-light rounded-end-2 ${
                              formValues.zipValid === false ? "is-invalid" : ""
                            }`}
                            placeholder="Pincode"
                            value={formValues.zip}
                            onChange={handleChange}
                          />
                          {formValues.zipValid === false && (
                            <div className="invalid-feedback text-start">
                              6 Digit pincode is required.
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    {hasValidZip && onlineDiscountTotal > 0 && (
                      <div className="alert alert-success text-start mt-2 py-2 mb-3">
                        <strong>Pay Online</strong> and get this for &#8377;
                        {onlineTotal} (Save &#8377;{onlineDiscountTotal})
                      </div>
                    )}
                    {iscreateAccount && (
                      <Form.Group
                        controlId="createAccountCheckbox"
                        className="mt-3 text-start"
                      >
                        <Form.Check
                          type="checkbox"
                          label="Create an account?"
                          checked={createAccount}
                          onChange={handleCheckboxChange}
                        />
                      </Form.Group>
                    )}
                    {createAccount && (
                      <>
                        <Row>
                          <Col className="position-relative">
                            <Form.Group controlId="password">
                              <Form.Label className="mt-2 fs-6 text-start w-100">
                                Password
                              </Form.Label>
                              <Form.Control
                                type={showPassword ? "text" : "password"}
                                className={`bg-light rounded-end-2 ${
                                  formValues.passwordValid === false
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Enter password"
                                value={formValues.password}
                                onChange={(e) => {
                                  handleChange(e);
                                  calculatePasswordStrength(e.target.value);
                                }}
                              />
                              {formValues.passwordValid === false && (
                                <div className="invalid-feedback text-start">
                                  Password is required.
                                </div>
                              )}
                            </Form.Group>
                            <span
                              onClick={toggleShowPassword}
                              className="position-absolute end-0 translate-middle-y me-5"
                              style={{ top: "60px" }}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </Col>
                          <Col className="position-relative">
                            <Form.Group controlId="confirmPassword">
                              <Form.Label className="mt-2 fs-6 text-start w-100">
                                Confirm Password
                              </Form.Label>
                              <Form.Control
                                type={showConfirmPassword ? "text" : "password"}
                                className={`bg-light rounded-end-2 ${
                                  formValues.confirmPasswordValid === false ||
                                  validatePasswordMatch === false
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Confirm password"
                                value={formValues.confirmPassword}
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                              />
                              {(formValues.confirmPasswordValid === false ||
                                validatePasswordMatch === false) && (
                                <div className="invalid-feedback text-start">
                                  Passwords must match.
                                </div>
                              )}
                            </Form.Group>
                            <span
                              onClick={toggleShowConfirmPassword}
                              className="position-absolute end-0 translate-middle-y me-5"
                              style={{ top: "60px" }}
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card style={{ position: "sticky", top: "18vh" }}>
                <Card.Body>
                  {/* Order Summary */}
                  <h5 className="fr1">Order Summary</h5>
                  <Row className="mt-3">
                    <Col className="text-start">Quantity</Col>
                    <Col className="text-end mb-2">{totalQuantity}</Col>
                  </Row>
                  <Row>
                    <Col className="text-start">MRP Total</Col>
                    <Col className="text-end mb-2">&#8377;{totalMRP}</Col>
                  </Row>
                  {totalDiscount > 0 && (
                    <Row>
                      <Col className="text-start">Product Discount</Col>
                      <Col className="text-end mb-2">
                        −&#8377;{totalDiscount}
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col className="text-start">
                      <strong>Item Total</strong>
                    </Col>
                    <Col className="text-end mb-2">
                      <strong>&#8377;{totalAmount}</strong>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-start">Shipping</Col>
                    <Col className="text-end mb-2">
                      {shippingPrice === 0
                        ? "Enter Pincode to calculate"
                        : `+₹${shippingPrice}`}
                    </Col>
                  </Row>
                  {couponEligible && (
                    <>
                      <Row className="mt-2">
                        <Col xs={12} className="text-start">
                          <Form.Label className="mb-1 fr05">
                            Have a coupon code?
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="text"
                              placeholder="Enter coupon code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              disabled={!!appliedCoupon}
                            />
                            {appliedCoupon ? (
                              <Button
                                variant="outline-danger"
                                onClick={handleRemoveCoupon}
                              >
                                Remove
                              </Button>
                            ) : (
                              <Button
                                variant="outline-primary"
                                onClick={handleApplyCoupon}
                                disabled={couponLoading}
                              >
                                {couponLoading ? "Applying..." : "Apply"}
                              </Button>
                            )}
                          </InputGroup>
                          {couponMessage && (
                            <small className="text-danger d-block mt-1">
                              {couponMessage}
                            </small>
                          )}
                        </Col>
                      </Row>
                      {appliedCoupon && (
                        <Row className="mt-2">
                          <Col className="text-start text-success">
                            Coupon "{appliedCoupon.code}" applied
                          </Col>
                          <Col className="text-end mb-2 text-success">
                            −&#8377;{couponDiscountTotal}
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  <hr />
                  <Row className="mt-3">
                    <Col className="text-start">
                      <strong>Total Amount Payable</strong>
                    </Col>
                    <Col className="text-end mb-2">
                      <strong>&#8377;{finalTotal}</strong>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <small className="text-muted">
                      {paymentMethod === "payNow" ? "Paying online" : "Cash on Delivery"}
                    </small>
                  </div>
                  <hr />
                  {/* Payment Section */}
                  <h5 className="text-start">Payment</h5>
                  <div className="text-start">
                    All transactions are secure and encrypted.
                  </div>
                  <Card className="my-3 p-3">
                    <Card.Title className="fr1">
                      Razorpay Secure (UPI, Cards, Wallets, NetBanking)
                    </Card.Title>
                    <Card.Text>
                      <img
                        src={razorPay}
                        alt="Razorpay"
                        style={{ width: "100%" }}
                      />
                    </Card.Text>
                  </Card>
                  {/* Pay Now */}
                  <label
                    className={`d-flex flex-column alert p-3 my-2 ${
                      paymentMethod === "payNow" ? "alert-info" : "alert-light"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="radio"
                        value="payNow"
                        checked={paymentMethod === "payNow"}
                        onChange={() => handlePaymentMethodChange("payNow")}
                        className="me-2"
                      />
                      <strong>Pay Now</strong>
                      {onlineDiscountTotal > 0 && (
                        <span className="ms-auto text-success">
                          &#8377;{onlineTotal} (Save &#8377;
                          {onlineDiscountTotal})
                        </span>
                      )}
                    </div>
                    <small className="text-muted text-start mt-1">
                      Pay securely via Razorpay, supporting UPI, Credit/Debit
                      Cards, and Net Banking.
                    </small>
                  </label>

                  {/* Cash on Delivery */}
                  <label
                    className={`d-flex flex-column alert p-3 mb-2 ${
                      paymentMethod === "cod" ? "alert-info" : "alert-light"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="radio"
                        value="cod"
                        checked={paymentMethod === "cod"}
                        onChange={() => handlePaymentMethodChange("cod")}
                        className="me-2"
                        // disabled={!codEligible} // Disable if COD is not available
                      />
                      <strong>Cash on Delivery</strong>
                      {onlineDiscountTotal > 0 && (
                        <span className="ms-auto">&#8377;{codTotal}</span>
                      )}
                    </div>
                    <small className="text-muted text-start mt-1">
                      Pay when you receive the product.
                    </small>
                    {codEligible === false && (
                      <small className="text-danger text-start mt-1">
                        COD is not available for this pincode.
                      </small>
                    )}
                  </label>
                  <Button
                    className="w-100 btn"
                    style={{ background: "#CC0000", border: "none" }}
                    variant="primary"
                    disabled={!isFormValid}
                    onClick={handlePayment}
                  >
                    Proceed with payment
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Checkout;
